/* eslint-disable */
import { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Typography,
  List,
  ListItemButton,
  ListItemText
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {
  currentKwVolumeAmazonCategoryList,
  fetchKwVolumeScMasterChildren,
  kwVolChangeAcate1,
  kwVolChangeAcate2,
  kwVolChangeAcate3,
  kwVolChangeAcate4,
  kwVolChangeAcate5,
  kwVolChangeAcate6,
  kwVolScMasterChildrenStatusSelector
} from 'feactures/categoryKeywordVolume/categoryKeywordVolumeSlice';
import {
  MAX_KW_VOLUME_TIERS,
  getKwVolumePathDepth,
  getKwVolumeNextLevelItems
} from 'feactures/categoryKeywordVolume/kwVolumePickerHelpers';

export default function KwVolumeCategoryForm({
  categories,
  comboGrayscaleIds
}) {
  const dispatch = useDispatch();
  const currentSelectCategoryList = useSelector(currentKwVolumeAmazonCategoryList);
  const childrenLoading =
    useSelector(kwVolScMasterChildrenStatusSelector) === 'loading';

  const kwDepth = useMemo(
    () => getKwVolumePathDepth(currentSelectCategoryList),
    [currentSelectCategoryList]
  );

  const grayscaleSet = useMemo(() => {
    if (!Array.isArray(comboGrayscaleIds) || comboGrayscaleIds.length === 0) {
      return null;
    }
    return new Set(comboGrayscaleIds.map((x) => Number(x)));
  }, [comboGrayscaleIds]);

  const itemIsGrayscale = useCallback(
    (item) => grayscaleSet && grayscaleSet.has(Number(item.value)),
    [grayscaleSet]
  );

  const dispatchKwVolumeCategoryChange = useCallback(
    (level, selected) => {
      if (level === 1) dispatch(kwVolChangeAcate1(selected));
      else if (level === 2) dispatch(kwVolChangeAcate2(selected));
      else if (level === 3) dispatch(kwVolChangeAcate3(selected));
      else if (level === 4) dispatch(kwVolChangeAcate4(selected));
      else if (level === 5) dispatch(kwVolChangeAcate5(selected));
      else if (level === 6) dispatch(kwVolChangeAcate6(selected));
    },
    [dispatch]
  );

  const dispatchKwVolumeScChildren = useCallback(
    (parentId, level) => {
      dispatch(
        fetchKwVolumeScMasterChildren({
          parent_id: parentId,
          level,
          marketplace: 'JP'
        })
      );
    },
    [dispatch]
  );

  const truncateKwTier = useCallback(
    (tierIndex) => {
      const clickedDeepest = kwDepth > 0 && tierIndex === kwDepth - 1;
      if (clickedDeepest) {
        const clears = [
          kwVolChangeAcate1,
          kwVolChangeAcate2,
          kwVolChangeAcate3,
          kwVolChangeAcate4,
          kwVolChangeAcate5,
          kwVolChangeAcate6
        ];
        dispatch(clears[tierIndex](0));
        return;
      }

      const sel = currentSelectCategoryList[tierIndex];
      if (
        sel == null ||
        sel === '' ||
        sel === 0 ||
        (typeof sel === 'object' &&
          (sel.value == null || sel.value === ''))
      ) {
        return;
      }

      const actions = [
        kwVolChangeAcate1,
        kwVolChangeAcate2,
        kwVolChangeAcate3,
        kwVolChangeAcate4,
        kwVolChangeAcate5,
        kwVolChangeAcate6
      ];
      dispatch(actions[tierIndex](sel));
    },
    [currentSelectCategoryList, dispatch, kwDepth]
  );

  const onCategoryItemClick = useCallback(
    (level, selected) => {
      if (
        !selected ||
        typeof selected !== 'object' ||
        selected.value == null ||
        selected.value === '' ||
        itemIsGrayscale(selected)
      ) {
        return;
      }
      dispatchKwVolumeCategoryChange(level, selected);
      if (level < MAX_KW_VOLUME_TIERS) {
        dispatchKwVolumeScChildren(selected.value, level + 1);
      }
    },
    [dispatchKwVolumeCategoryChange, dispatchKwVolumeScChildren, itemIsGrayscale]
  );

  const kwNextLevel = kwDepth + 1;
  const kwNextItems = getKwVolumeNextLevelItems(categories, kwDepth);
  const kwAllTiersFilled = kwDepth >= MAX_KW_VOLUME_TIERS;
  const kwAwaitingNextTierList =
    childrenLoading && kwDepth > 0 && kwNextLevel > 1;

  const breadcrumbKwSegments = useMemo(() => {
    if (kwDepth === 0) return [];
    const out = [];
    for (let i = 0; i < kwDepth; i += 1) {
      const slot = currentSelectCategoryList[i];
      if (slot && typeof slot === 'object' && slot.label) {
        out.push({ label: slot.label, idx: i });
      }
    }
    return out;
  }, [kwDepth, currentSelectCategoryList]);

  const showTierList =
    !kwAllTiersFilled &&
    kwNextLevel <= MAX_KW_VOLUME_TIERS &&
    kwNextLevel >= 1 &&
    (kwNextItems.length > 0 || kwAwaitingNextTierList);

  const hasGrayscaleInList = kwNextItems.some((item) => itemIsGrayscale(item));

  return (
    <Box>
      {breadcrumbKwSegments.length > 0 && (
        <Breadcrumbs separator="›" sx={{ mb: 1, flexWrap: 'wrap' }}>
          {breadcrumbKwSegments.map((seg) => (
            <Link
              key={`kw-bc-${seg.idx}`}
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              sx={{
                cursor: 'pointer',
                border: 0,
                background: 'none',
                padding: 0,
                font: 'inherit',
                textAlign: 'left'
              }}
              onClick={() => truncateKwTier(seg.idx)}
            >
              {seg.label}
            </Link>
          ))}
        </Breadcrumbs>
      )}

      {showTierList && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {kwDepth === 0 ? 'カテゴリを選んでください' : 'さらに下の階層を選べます'}
        </Typography>
      )}

      {showTierList && kwAwaitingNextTierList && kwNextItems.length === 0 && (
        <Box display="flex" alignItems="center" gap={1} py={0.5}>
          <CircularProgress size={18} />
          <Typography variant="caption" color="text.secondary">
            読み込み中…
          </Typography>
        </Box>
      )}

      {showTierList && !(kwAwaitingNextTierList && kwNextItems.length === 0) && (
        <List
          dense
          disablePadding
          sx={{
            maxHeight: 280,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper'
          }}
        >
          {kwNextItems.map((item) => {
            const grey = itemIsGrayscale(item);
            return (
              <ListItemButton
                key={item.value}
                disabled={grey}
                title={grey ? 'このカテゴリは対象外です' : item.label}
                onClick={() => onCategoryItemClick(kwNextLevel, item)}
                sx={{
                  py: 0.75,
                  '&:hover': grey ? undefined : { bgcolor: 'action.hover' }
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    color: grey ? 'text.disabled' : 'text.primary',
                    noWrap: true
                  }}
                />
                {!grey && (
                  <ChevronRightIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary', flexShrink: 0 }}
                  />
                )}
              </ListItemButton>
            );
          })}
        </List>
      )}

      {showTierList && hasGrayscaleInList && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5 }}
        >
          薄い色のカテゴリは対象外です。
        </Typography>
      )}
    </Box>
  );
}
